import { Box, Button, Container, Typography, Stack, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ROUTES, APP_NAME, UI_LANGUAGES } from '../config/constants';
import { useAuthStore } from '../store/authStore';
import { useEffect } from 'react';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import TranslateIcon from '@mui/icons-material/Translate';
import LoopIcon from '@mui/icons-material/Loop';
import SpeedIcon from '@mui/icons-material/Speed';
import { SEO } from '../components/SEO';

export const Welcome = () => {
  const { t, i18n } = useTranslation('welcome');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const handleLanguageChange = async (languageCode: string) => {
    await i18n.changeLanguage(languageCode);
    localStorage.setItem('i18nextLng', languageCode);
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTES.DASHBOARD);
    }
  }, [isAuthenticated, navigate]);

  const features = [
    {
      icon: <HeadphonesIcon sx={{ fontSize: 48 }} />,
      title: t('features.discover.title'),
      description: t('features.discover.description'),
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 48 }} />,
      title: t('features.playback.title'),
      description: t('features.playback.description'),
    },
    {
      icon: <LoopIcon sx={{ fontSize: 48 }} />,
      title: t('features.loop.title'),
      description: t('features.loop.description'),
    },
    {
      icon: <TranslateIcon sx={{ fontSize: 48 }} />,
      title: t('features.transcribe.title'),
      description: t('features.transcribe.description'),
    },
  ];

  return (
    <>
      <SEO 
        title="Scribelate - Learn Languages Through Podcasts"
        description="Master Spanish, English, and Japanese through authentic podcast content. Control playback speed, loop audio segments, transcribe and translate content, and build vocabulary with spaced repetition."
        keywords="language learning, podcast learning, learn spanish, learn japanese, learn english, podcast app, transcription, translation, vocabulary builder, audio learning"
        canonical="https://scribelate.com/"
      />
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
        {/* Language Selector */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1 }}>
            <InputLabel sx={{ color: 'white', '&.Mui-focused': { color: 'white' } }}>{t('changeLanguage')}</InputLabel>
            <Select
              value={i18n.language}
              label={t('changeLanguage')}
              onChange={(e) => handleLanguageChange(e.target.value)}
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '.MuiSvgIcon-root': { color: 'white' },
              }}
            >
              {UI_LANGUAGES.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  {language.nativeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        {/* Header */}
        <Box textAlign="center" mb={8}>
          <Typography variant="h1" component="h1" gutterBottom fontWeight="bold">
            {APP_NAME}
          </Typography>
          <Typography variant="h5" component="h2" mb={4} sx={{ opacity: 0.9 }}>
            {t('hero.title')}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(ROUTES.REGISTER)}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' },
                px: 4,
                py: 1.5,
              }}
            >
              {t('hero.getStarted')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate(ROUTES.LOGIN)}
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                px: 4,
                py: 1.5,
              }}
            >
              {t('hero.signIn')}
            </Button>
          </Stack>
        </Box>

        {/* Features */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }}
          gap={4}
          mt={8}
        >
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  bgcolor: 'rgba(255,255,255,0.15)',
                },
              }}
            >
              <Box mb={2}>{feature.icon}</Box>
              <Typography variant="h6" gutterBottom fontWeight="500">
                {feature.title}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {feature.description}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* About Section */}
        <Box
          mt={10}
          p={4}
          sx={{
            bgcolor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
          }}
        >
          <Typography variant="h4" gutterBottom textAlign="center" fontWeight="500">
            {t('howItWorks.title')}
          </Typography>
          <Typography variant="body1" paragraph sx={{ opacity: 0.9, mt: 3 }}>
            {t('howItWorks.description1')}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {t('howItWorks.description2')}
          </Typography>
        </Box>

        {/* Footer */}
        <Box 
          sx={{ 
            mt: 8, 
            pt: 4, 
            borderTop: '1px solid rgba(255,255,255,0.2)',
            textAlign: 'center'
          }}
        >
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={3} 
            justifyContent="center"
            mb={2}
          >
            <Button 
              onClick={() => navigate(ROUTES.HELP)}
              sx={{ color: 'white', textTransform: 'none' }}
            >
              {t('footer.help')}
            </Button>
            <Button 
              onClick={() => navigate(ROUTES.PRIVACY)}
              sx={{ color: 'white', textTransform: 'none' }}
            >
              {t('footer.privacy')}
            </Button>
            <Button 
              onClick={() => navigate(ROUTES.TERMS)}
              sx={{ color: 'white', textTransform: 'none' }}
            >
              {t('footer.terms')}
            </Button>
            <Button 
              onClick={() => navigate(ROUTES.DMCA)}
              sx={{ color: 'white', textTransform: 'none' }}
            >
              {t('footer.dmca')}
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            © 2026 {APP_NAME}. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
    </>
  );
};
